import { FC, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TableContainer,
  TableHead,
  TableRow,
  Table,
  TableCell,
  TableBody,
  IconButton,
} from '@mui/material';
import { useQuery } from '@apollo/client';
import LoadingSpinner from '../../../LoadingSpinner';
import Fuse from 'fuse.js';
import { SearchField } from '../../../components/SearchField';
import React from 'react';
import { GetAllStandardsQuery } from '../../../__generated__/graphql';
import { Unpacked } from '../../../graphQLTypes/types';
import { Modal, useModal } from '../../../dialogs/useModal';
import { ALL_STANDARDS } from '../../../standards/standards.graphql';
import { Add, Check } from '@mui/icons-material';
import { AddStandardDialog, StandardAdded } from '../../../standards/AddStandardDialog';
import { useGuideCmsContext } from '../GuideCmsContext';

export interface StandardLinkDialogResult {
  id: string;
  name: string;
}

type Standards = GetAllStandardsQuery['standards'];
type Standard = Unpacked<Standards>;

export interface StandardLinkDialogProps {
  selectedText?: string;
}

export const StandardLinkDialog: FC<Modal<StandardLinkDialogProps, StandardLinkDialogResult>> = (modal) => {
  const { close } = modal;
  const { editor } = useGuideCmsContext();
  const [filter, setFilter] = useState(modal.data.selectedText ?? '');
  const { data, loading, refetch } = useQuery(ALL_STANDARDS);
  const standards = data?.standards;

  const handleOnClose = () => {
    close();
  };

  const filterStandards = (standards: Standard[]): Standard[] => {
    let filtered = standards;
    if (!filter) return filtered;

    var options = {
      shouldSort: false,
      tokenize: true,
      matchAllTokens: true,
      threshold: 0,
      keys: ['name', 'title', 'version', 'language'],
    };

    var fuse = new Fuse(filtered, options);
    filtered = fuse.search(filter.trim()).map((x) => x.item);
    return filtered;
  };
  const addModal = useModal<undefined, StandardAdded>({ data: undefined });
  const addStandard = async () => {
    const result = await addModal.open();
    if (result) {
      refetch();
    }
  };
  const filteredStandards = standards ? filterStandards(standards) : [];

  const addStandardLink = (payload: StandardLinkDialogResult) => {
    editor
      ?.chain()
      .focus()
      .insertContent({
        type: 'standardLink',
        attrs: {
          standardId: payload.id,
          standardName: payload.name.split(':')[0],
        },
      })
      .run();
  };

  return (
    <>
      {addModal.isOpen && <AddStandardDialog modal={addModal} />}
      <Dialog open={true} onClose={handleOnClose} disableRestoreFocus maxWidth="xl" fullWidth={true}>
        <DialogTitle>
          Legg til lenke til standard
          <IconButton onClick={addStandard} title="Legg til ny standard" size="large">
            <Add />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <SearchField filter={setFilter} startSearch={filter} />
          {loading && <LoadingSpinner />}
          {!loading && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Velg</TableCell>
                    <TableCell>Gyldig</TableCell>
                    <TableCell>ID</TableCell>
                    <TableCell>Tittel</TableCell>
                    <TableCell>Utgave</TableCell>
                    <TableCell>Språk</TableCell>
                    <TableCell>ErstattetAv(EA)/ Erstatter(E)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredStandards.map((standard) => {
                    if (!standard) return null;
                    return (
                      <TableRow key={standard.id}>
                        <TableCell>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => {
                              const result: StandardLinkDialogResult = {
                                id: standard.id,
                                name: standard.name ?? '',
                              };
                              addStandardLink(result);
                              close(result);
                            }}>
                            Velg
                          </Button>
                        </TableCell>
                        <TableCell>{standard.isValid && <Check fontSize="small" />}</TableCell>
                        <TableCell sx={{ minWidth: '150px', overflowWrap: 'anywhere' }}>{standard.name}</TableCell>
                        <TableCell>{standard.title}</TableCell>
                        <TableCell sx={{ minWidth: '130px' }}>{standard.version}</TableCell>
                        <TableCell>{standard.language}</TableCell>
                        <TableCell sx={{ minWidth: '150px', overflowWrap: 'anywhere' }}>{standard.link}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleOnClose}>Avbryt</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
